/**
 * Owns when the cart drawer may close: it stops the theme closing it by
 * itself, and it adds the tap-outside-to-dismiss the theme never wired up.
 * Both halves share one idea of what counts as the shopper's own action,
 * which is why they live in one file.
 *
 * ---------------------------------------------------------------------------
 * 1. KEEPING IT OPEN
 *
 * Stock Horizon behaviour, reproduced on the unmodified live theme: removing
 * a line can empty the cart and then, a beat later, close the whole drawer.
 * From the shopper's side nothing explains it -- they tapped a small x on one
 * line and the entire panel vanished, taking the empty-cart message and the
 * close button with it.
 *
 * WHY IT WATCHES INSTEAD OF HOOKING THE CLICK. Two early versions keyed off
 * the Remove button's click and neither worked: the first polled at 400ms and
 * 1200ms and found the drawer still open both times, and the second armed a
 * MutationObserver from a capture-phase click listener that never ran. So
 * this stops trying to predict the trigger and watches for the drawer closing
 * when nobody asked it to.
 *
 * WHY IT DOES NOT BLOCK close() INSTEAD. Preventing the close outright would
 * avoid the brief flash this leaves. It was tried and it breaks the drawer:
 * swallowing close() desyncs ThemeDrawer's internal state from the dialog, so
 * the NEXT close -- the shopper's own, on the x button -- silently does
 * nothing and the drawer can no longer be dismissed at all.
 *
 * THE BUG THIS FILE ONCE HAD. The first working version refused to re-open
 * more than once per 4000ms, meaning to avoid an open/close loop. With three
 * items in the cart that reads as: remove one, fine; remove the next a second
 * later, ejected; remove the last after the window lapses, fine again --
 * exactly how it was reported. A re-open cannot loop on its own; it only
 * loops if each re-open provokes another close. So the guard now looks for
 * THAT -- several re-opens bunched inside a second and a half.
 *
 * ---------------------------------------------------------------------------
 * 2. LETTING IT CLOSE -- AND THE TWO OPEN MODES
 *
 * The drawer opens two different ways, and they are not equivalent:
 *
 *   opened from the cart icon   -> modal dialog. A ::backdrop covers the page
 *                                  and the page behind is inert.
 *   auto-opened after Add to cart -> NOT modal. No backdrop is painted and the
 *                                  page behind stays fully clickable.
 *
 * Neither sets a `closedby` attribute, so there is no native light-dismiss,
 * and the theme registers no outside-click handler of its own. Tapping beside
 * the panel did nothing in either mode.
 *
 * On a phone that was the whole exit: `--theme-drawer-width` is 30rem with
 * `max-width: 100%`, so the panel covered a 375px screen edge to edge and the
 * small x was the only way back to the shop. The width cap that exposes a
 * strip lives in snippets/cart-drawer.liquid; this is the half that makes the
 * strip do something.
 *
 * Handling both modes needs two different tests for "outside", because the
 * event target differs:
 *   modal      -> a tap on the backdrop arrives with the DIALOG as its target,
 *                 so outside means the pointer is beyond the dialog's own box.
 *   not modal  -> the tap lands on whatever page element is under it, so
 *                 outside means the target is not inside the drawer at all.
 *
 * The outside tap is consumed (preventDefault + stopPropagation, hence the
 * capture phase). In the non-modal mode the exposed strip sits over live page
 * content, and a first tap that both dismissed the drawer AND followed a
 * product link underneath would be worse than the bug. Add-to-cart controls
 * are exempt so tapping one with the drawer open still adds.
 *
 * ---------------------------------------------------------------------------
 * WHOSE CLOSE IS IT. A close is the shopper's if they just pressed Escape,
 * pressed a close button, tapped outside, or followed a link or the checkout
 * button. Gestures are marked on pointerdown, not click, because the theme's
 * own delegated click handler is registered at page load and runs before
 * anything added later -- keying on click loses the race and the drawer
 * refuses to close on its own x button.
 */
(function () {
  'use strict';

  /** A close within this long after a real gesture belongs to the shopper. */
  var GESTURE_MS = 900;
  /** Re-opens bunched this tightly, this many times, mean we are in a loop. */
  var LOOP_WINDOW_MS = 1500;
  var LOOP_LIMIT = 4;

  var lastGesture = 0;
  var recentReopens = [];

  /**
   * A cart-line update that began while the drawer was open temporarily owns
   * the close lifecycle. Mobile cart morphs can close the underlying dialog
   * after removing a line; that is not a request to dismiss the drawer.
   * A real close gesture recorded after the update began still wins.
   */
  var cartUpdateStartedAt = 0;
  var keepOpenUntil = 0;

  function markGesture() {
    lastGesture = Date.now();
  }

  function host() {
    return document.querySelector('theme-drawer#cart-drawer');
  }

  function isOpen(drawer) {
    if (!drawer) return false;
    var dialog = drawer.querySelector('dialog');
    return (
      drawer.hasAttribute('open') ||
      Boolean(dialog && dialog.hasAttribute('open'))
    );
  }

  function reopenAfterCartUpdate(startedAt) {
    if (lastGesture > startedAt) return;

    var drawer = host();
    if (
      !drawer ||
      typeof drawer.open !== 'function' ||
      isOpen(drawer)
    ) {
      return;
    }

    drawer.open();
  }

  /**
   * Section morphing replaces the drawer node and may finish its close
   * animation well after the remove click. Retry against the current host for
   * the full update window; theme-drawer.open() safely defers while closing.
   */
  function scheduleCartReopen(startedAt) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        reopenAfterCartUpdate(startedAt);
      });
    });

    [80, 220, 500, 900, 1500, 2400].forEach(function (delay) {
      setTimeout(function () {
        reopenAfterCartUpdate(startedAt);
      }, delay);
    });
  }

  function armCartUpdateKeepOpen() {
    var drawer = host();
    if (!drawer || !isOpen(drawer)) return 0;

    var startedAt = Date.now();
    cartUpdateStartedAt = startedAt;
    keepOpenUntil = startedAt + 3200;
    scheduleCartReopen(startedAt);
    return startedAt;
  }

  var GESTURE_SELECTOR =
    '.theme-drawer__close-button, .cart-items__empty-button, ' +
    '.cart__checkout-button, a[href]';

  /**
   * Controls that legitimately open or update the drawer must never be treated
   * as an outside click. Horizon handles the cart trigger before this capture
   * listener, so without this exemption the drawer opens and closes again in
   * the same click.
   */
  var EXEMPT_SELECTOR =
    '.add-to-cart-button, [ref="addToCartButton"], ' +
    '[data-testid="cart-drawer-trigger"], [aria-controls="cart-drawer"]';

  // pointerdown/mousedown/touchstart all fire before click, so the gesture is
  // recorded before any click handler -- ours or the theme's -- can run.
  ['pointerdown', 'mousedown', 'touchstart'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        var t = event.target;
        if (!t || typeof t.closest !== 'function') return;
        if (t.closest(GESTURE_SELECTOR) || t.tagName === 'DIALOG') markGesture();
      },
      true
    );
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') markGesture();
  }, true);

  // Leaving the page closes drawers legitimately.
  window.addEventListener('pagehide', markGesture);
  window.addEventListener('beforeunload', markGesture);

  /**
   * Removing a line is never a request to dismiss the cart. Arm the keep-open
   * window in capture phase, before Horizon starts the cart request or replaces
   * the drawer markup. This also covers removal of the final line so the empty
   * state remains visible until the shopper closes it.
   */
  document.addEventListener(
    'click',
    function (event) {
      var t = event.target;
      if (!t || typeof t.closest !== 'function') return;
      if (!t.closest('.cart-items__remove')) return;
      armCartUpdateKeepOpen();
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var drawer = host();
      if (!drawer) return;
      var dialog = drawer.querySelector('dialog');
      if (!dialog || !dialog.hasAttribute('open')) return;
      // detail is 0 for clicks synthesised from the keyboard, whose
      // coordinates are 0,0 and would read as outside every time.
      if (!event.detail) return;

      var t = event.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest(EXEMPT_SELECTOR)) return;

      if (t === dialog) {
        // Modal mode: the backdrop reports the dialog as the target, but so
        // does the panel's own padding. Only a point beyond the box is
        // genuinely outside.
        var box = dialog.getBoundingClientRect();
        var outside =
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom;
        if (!outside) return;
      } else if (t.closest('theme-drawer#cart-drawer')) {
        return; // a real click on something in the panel
      }

      markGesture();
      event.preventDefault();
      event.stopPropagation();
      if (typeof drawer.close === 'function') drawer.close();
    },
    true
  );

  /**
   * Cart line updates are the reliable signal the stock theme exposes. Record
   * whether an update started while the drawer was open, then reopen after the
   * section morph settles. Repeated short checks cover the theme's async close
   * animation; open() safely defers itself when a close is already in flight.
   */
  document.addEventListener(
    'shopify:cart:lines-update',
    function (event) {
      var drawer = host();
      if (!drawer || !isOpen(drawer)) return;
      if (event.action !== 'remove' && event.action !== 'update') return;

      var startedAt = Date.now();
      cartUpdateStartedAt = startedAt;
      keepOpenUntil = startedAt + 2500;

      var reopen = function () {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            reopenAfterCartUpdate(startedAt);
          });
        });

        setTimeout(function () {
          reopenAfterCartUpdate(startedAt);
        }, 250);

        setTimeout(function () {
          reopenAfterCartUpdate(startedAt);
        }, 700);
      };

      if (event.promise && typeof event.promise.then === 'function') {
        event.promise.then(reopen, function () {});
      } else {
        reopen();
      }
    },
    true
  );

  // Watching the whole document rather than the drawer node: the drawer's
  // markup is re-rendered by the Section Rendering API, so a reference taken
  // once goes stale. attributeFilter keeps this cheap -- it only wakes for
  // `open`, which almost nothing else on the page uses.
  var observer = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var node = records[i].target;
      if (!node || node.tagName !== 'DIALOG') continue;

      var drawer = host();
      if (!drawer || !drawer.contains(node)) continue;
      if (node.hasAttribute('open')) continue;

      var now = Date.now();
      var protectedCartUpdate =
        now < keepOpenUntil && lastGesture <= cartUpdateStartedAt;
      if (!protectedCartUpdate && now - lastGesture < GESTURE_MS) return; // they closed it

      recentReopens = recentReopens.filter(function (t) {
        return now - t < LOOP_WINDOW_MS;
      });
      if (recentReopens.length >= LOOP_LIMIT) return; // fighting a loop
      recentReopens.push(now);

      if (typeof drawer.open === 'function') drawer.open();
      return;
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    subtree: true,
    attributeFilter: ['open'],
  });
})();
